import { useMemo } from 'react';
import type React from 'react';

import { Box } from '@change-corgi/design-system-legacy/layout';

import type { OnlineShareChannel } from '#src/app/pages/starterDashboard/shared/types';
import { ShareButton } from '#src/app/shared/components/share';
import type { ShareAction } from '#src/app/shared/hooks/share';

import { SHARE_BUTTON_CONFIG } from '../../../useDashboardShareActions/constants';

type OnlineShareAction = ShareAction<OnlineShareChannel>;
type Props = { actions: readonly OnlineShareAction[]; closeSharePetitionModalFn?: () => void | Promise<void> };

// These channels open an educational modal — the share petition modal must close first to avoid stacked modals
const CHANNELS_WITH_EDUCATION_MODAL: readonly OnlineShareChannel[] = ['instagram'];

function Button({
	action,
	index,
	closeSharePetitionModalFn,
}: {
	action: OnlineShareAction;
	index: number;
	closeSharePetitionModalFn?: () => void | Promise<void>;
}) {
	const { channel } = action;

	const { label, successLabel } = SHARE_BUTTON_CONFIG[channel];

	const buttonConfig = useMemo(
		() => ({
			...action,
			label,
			successLabel,
		}),
		[action, label, successLabel],
	);

	return (
		<Box
			as="li"
			role="listitem"
			data-qa={`sfs-${channel}-button`}
			sx={{ gridColumn: index === 0 ? 'span 2' : undefined }}
		>
			<ShareButton buttonConfig={buttonConfig} onBeforeAction={closeSharePetitionModalFn} />
		</Box>
	);
}

export const ActionItems: React.FC<Props> = ({ actions, closeSharePetitionModalFn }) => {
	return (
		<Box
			as="ul"
			role="list"
			sx={{
				display: 'grid',
				gridGap: 12,
				mt: 24,
				gridTemplateColumns: '1fr 1fr',
			}}
		>
			{actions.map((action, index) => (
				<Button
					key={action.channel}
					data-qa={`sfs-${action.channel}-action-item`}
					action={action}
					index={index}
					closeSharePetitionModalFn={
						CHANNELS_WITH_EDUCATION_MODAL.includes(action.channel) ? closeSharePetitionModalFn : undefined
					}
				/>
			))}
		</Box>
	);
};
